/**
 * AI Reactive Phases — DI, burst, edgeguard, and defensive movement.
 *
 * These run when the AI is the *defending* or *reacting* player,
 * not the active turn-taker. Injects directional keys via AIInput.
 *
 * Decide-once pattern: decisions are made once at phase entry after a
 * difficulty-scaled reaction delay, then held for the duration.
 *
 * Uses seeded RNG (not Math.random) — AI decisions affect game state.
 */

import { Keys } from 'bonkjs';
import { ACTION, WORLD } from '../constants';
import type { GameState } from '../types';
import { random } from '../terrain/rng';
import type { AIInput } from './ai-input';
import type { SkillProfile } from './skill-profile';

// ── ReactiveState ──────────────────────────────────────────────

export interface ReactiveState {
  phase: string;              // which phase this was init'd for
  activePlayer: number;       // which player (detects diHitQueue cycling)
  reactionTimer: number;      // countdown before acting
  decided: boolean;           // decision locked in
  // DI
  diDirX: number;
  diDirY: number;
  // Edgeguard
  edgeguardNoiseOffset: number;
  // Burst
  burstDirX: number;
  burstDirY: number;
  burstDirDown: boolean;
}

export function createReactiveState(): ReactiveState {
  return {
    phase: '',
    activePlayer: -1,
    reactionTimer: 0,
    decided: false,
    diDirX: 0,
    diDirY: 0,
    edgeguardNoiseOffset: 0,
    burstDirX: 0,
    burstDirY: 0,
    burstDirDown: false,
  };
}

/**
 * Initialize reactive state for a new phase entry.
 * Makes the one-time decision immediately (consumes seeded RNG),
 * stores the result, and sets the reaction timer.
 *
 * RNG call counts are deterministic per phase regardless of branch:
 *   DI:        1 (variance) + 2 (quality roll + angle roll) = 3
 *   Edgeguard: 1 (variance) + 1 (noise offset)             = 2
 *   Burst:     1 (variance)                                 = 1
 *   Defense:   1 (variance)                                 = 1
 */
export function initReactiveState(
  reactive: ReactiveState,
  state: GameState,
  phase: string,
  playerIndex: number,
  profile: SkillProfile,
): void {
  reactive.phase = phase;
  reactive.activePlayer = playerIndex;
  reactive.decided = true;

  // Reaction delay: reactionFrames ± variance, min 1
  const variance = Math.round((random() - 0.5) * 2 * profile.reactionVariance);
  reactive.reactionTimer = Math.max(1, profile.reactionFrames + variance);

  // Reset stored decisions
  reactive.diDirX = 0;
  reactive.diDirY = 0;
  reactive.edgeguardNoiseOffset = 0;
  reactive.burstDirX = 0;
  reactive.burstDirY = 0;
  reactive.burstDirDown = false;

  // Phase-specific one-time decision
  switch (phase) {
    case 'di_window':
      decideDI(reactive, state, playerIndex, profile);
      break;
    case 'edgeguard':
      decideEdgeguard(reactive, profile);
      break;
    case 'burst_window':
      decideBurst(reactive, state, playerIndex, profile);
      break;
    case 'defensive_movement':
      // No RNG beyond variance — decision is deterministic from game state
      break;
  }
}

// ── One-time decision helpers ──────────────────────────────────

/** DI: 2 RNG calls (quality roll + angle roll, always consumed). */
function decideDI(
  reactive: ReactiveState,
  state: GameState,
  playerIndex: number,
  profile: SkillProfile,
): void {
  const player = state.players[playerIndex];
  const kbVx = player.diPendingVx;
  const kbVy = player.diPendingVy;
  const kbSpeed = Math.sqrt(kbVx * kbVx + kbVy * kbVy);

  // Always consume 2 RNG calls for determinism
  const qualityRoll = random();
  const angleRoll = random();

  if (kbSpeed < 0.01) return; // no knockback — direction stays 0,0

  // Perpendicular directions to knockback
  const perpX1 = -kbVy / kbSpeed;
  const perpY1 = kbVx / kbSpeed;
  const perpX2 = kbVy / kbSpeed;
  const perpY2 = -kbVx / kbSpeed;

  // Pick perpendicular toward stage center
  const stageCenter = (state.stageLeft + state.stageRight) / 2;
  const stageCenterY = WORLD.VIRTUAL_HEIGHT * 0.4;
  const toCenter1 = (stageCenter - player.x) * perpX1 + (stageCenterY - player.y) * perpY1;
  const toCenter2 = (stageCenter - player.x) * perpX2 + (stageCenterY - player.y) * perpY2;

  let optimalX: number, optimalY: number;
  if (toCenter1 >= toCenter2) {
    optimalX = perpX1;
    optimalY = perpY1;
  } else {
    optimalX = perpX2;
    optimalY = perpY2;
  }

  // Degrade with diQuality: quality roll determines random vs optimal
  if (qualityRoll > profile.diQuality) {
    const randomAngle = angleRoll * Math.PI * 2;
    reactive.diDirX = Math.cos(randomAngle);
    reactive.diDirY = Math.sin(randomAngle);
  } else {
    reactive.diDirX = optimalX;
    reactive.diDirY = optimalY;
  }
}

/** Edgeguard: 1 RNG call (noise offset). */
function decideEdgeguard(
  reactive: ReactiveState,
  profile: SkillProfile,
): void {
  reactive.edgeguardNoiseOffset = (random() - 0.5) * 2 * profile.edgeguardAimNoise;
}

/** Burst: 0 extra RNG calls — decision is deterministic from game state. */
function decideBurst(
  reactive: ReactiveState,
  state: GameState,
  playerIndex: number,
  profile: SkillProfile,
): void {
  if (!profile.usesSpecial) return; // low-skill AI doesn't burst

  const player = state.players[playerIndex];
  const distToVoid = state.voidY - (player.y + 25);
  const stageCenter = (state.stageLeft + state.stageRight) / 2;

  if (distToVoid < 150) {
    // Near void — burst upward + toward center
    reactive.burstDirY = -1; // up
    reactive.burstDirX = player.x < stageCenter ? 1 : -1;
  } else if (player.grounded && player.damagePercent > 80) {
    // High damage grounded — brace (hold down)
    reactive.burstDirDown = true;
  }
  // else: neutral burst (all zeros)
}

// ── Per-frame handlers (called every fixed frame) ──────────────

/**
 * AI DI input — apply pre-computed direction after reaction delay.
 */
export function updateAIDI(
  state: GameState,
  playerIndex: number,
  input: AIInput,
  _profile: SkillProfile,
  reactive: ReactiveState,
): void {
  input.setKey(Keys.ArrowLeft, false);
  input.setKey(Keys.ArrowRight, false);
  input.setKey(Keys.ArrowUp, false);
  input.setKey(Keys.ArrowDown, false);

  if (reactive.reactionTimer > 0) {
    reactive.reactionTimer--;
    return;
  }

  const { diDirX, diDirY } = reactive;
  if (diDirX < -0.3) input.setKey(Keys.ArrowLeft, true);
  if (diDirX > 0.3) input.setKey(Keys.ArrowRight, true);
  if (diDirY < -0.3) input.setKey(Keys.ArrowUp, true);
  if (diDirY > 0.3) input.setKey(Keys.ArrowDown, true);
}

/**
 * AI Burst input — apply pre-computed burst direction after reaction delay.
 */
export function updateAIBurst(
  state: GameState,
  playerIndex: number,
  input: AIInput,
  profile: SkillProfile,
  reactive: ReactiveState,
): void {
  input.setKey(Keys.ArrowLeft, false);
  input.setKey(Keys.ArrowRight, false);
  input.setKey(Keys.ArrowUp, false);
  input.setKey(Keys.ArrowDown, false);

  if (!profile.usesSpecial) return;

  if (reactive.reactionTimer > 0) {
    reactive.reactionTimer--;
    return;
  }

  if (reactive.burstDirDown) {
    input.setKey(Keys.ArrowDown, true);
  } else {
    if (reactive.burstDirX < 0) input.setKey(Keys.ArrowLeft, true);
    if (reactive.burstDirX > 0) input.setKey(Keys.ArrowRight, true);
    if (reactive.burstDirY < 0) input.setKey(Keys.ArrowUp, true);
  }
}

/**
 * AI Edgeguard input — track target per-frame with locked noise offset.
 * Target angle is recomputed each frame (target is moving), but the
 * noise offset is decided once — smooth tracking, no jitter.
 */
export function updateAIEdgeguard(
  state: GameState,
  playerIndex: number,
  input: AIInput,
  _profile: SkillProfile,
  reactive: ReactiveState,
): void {
  input.setKey(Keys.ArrowUp, false);
  input.setKey(Keys.ArrowDown, false);
  input.setKey(ACTION.FIRE, false);
  input.setKey(ACTION.CHARGE, false);

  if (reactive.reactionTimer > 0) {
    reactive.reactionTimer--;
    return;
  }

  const attacker = state.players[playerIndex];
  const targets = state.edgeguardKnockedIndices;
  if (targets.length === 0) return;

  // Aim at the first knocked target
  const target = state.players[targets[0]];
  const dx = target.x - attacker.x;
  const dy = target.y - attacker.y;
  const targetAngle = Math.atan2(-dy, dx); // game angle convention
  const clampedTarget = targetAngle >= AIM_MIN ? targetAngle : targetAngle + Math.PI * 2;

  // Apply locked noise offset
  const noisyTarget = clampedTarget + reactive.edgeguardNoiseOffset;

  const diff = noisyTarget - attacker.angle;
  if (Math.abs(diff) > 0.03) {
    if (diff > 0) input.setKey(Keys.ArrowUp, true);
    else input.setKey(Keys.ArrowDown, true);
  }

  // Fire when charge is full
  if (state.edgeguardChargeTarget > 0 && state.edgeguardChargeFrame >= state.edgeguardChargeTarget) {
    input.setKey(ACTION.FIRE, true);
  }
}

// Aim angle bounds (full 360°)
const AIM_MIN = -Math.PI / 2;
const AIM_MAX = Math.PI * 3 / 2;

/**
 * AI Defensive Movement — move toward safe terrain after being knocked.
 * Air phase: drift toward stage center. Landing phase: walk toward center.
 * No reaction delay — defense starts immediately (reaction delay still ticks
 * but defense movement is simple enough to apply immediately).
 */
export function updateAIDefense(
  state: GameState,
  playerIndex: number,
  input: AIInput,
  _profile: SkillProfile,
  reactive: ReactiveState,
): void {
  const player = state.players[playerIndex];

  input.setKey(Keys.ArrowLeft, false);
  input.setKey(Keys.ArrowRight, false);
  input.setKey(Keys.A, false);
  input.setKey(Keys.D, false);

  if (reactive.reactionTimer > 0) {
    reactive.reactionTimer--;
    return;
  }

  const stageCenter = (state.stageLeft + state.stageRight) / 2;

  if (player.defenseSubPhase === 'air') {
    if (player.x < stageCenter - 50) {
      input.setKey(Keys.ArrowRight, true);
    } else if (player.x > stageCenter + 50) {
      input.setKey(Keys.ArrowLeft, true);
    }
  } else if (player.defenseSubPhase === 'landing') {
    if (player.x < stageCenter - 30) {
      input.setKey(Keys.D, true);
    } else if (player.x > stageCenter + 30) {
      input.setKey(Keys.A, true);
    }
  }
}
